import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ChoiceWheel from '../components/shared/ChoiceWheel';
import { PAYROLL_ENABLED, PAYROLL_PERMISSION } from '../config/payrollFeature';

// Settings and lookup lists, grouped off the Admin wheel so it stays about
// people and processes. Each tile keeps its own permission; the Admin wheel
// shows the Configuration tile when any of these would be shown here.
export const useConfigurationItems = () => {
  const auth = useAuth();

  const canManageAllEmployees = auth.hasPermission('manage_all_employees');
  // Hours Breakdown is a one-off reporting page with no permission of its own.
  const isAdmin = auth.isAdmin();
  const canManageVendors = auth.hasPermission('manage_expense_vendors');
  const canManageMileageRates = auth.hasPermission('manage_mileage_rates');
  // The PATO policy lives in the payroll module, so it follows that switch and
  // that permission - the same one the server checks to change it.
  const canManageTimeOff = PAYROLL_ENABLED && auth.hasPermission(PAYROLL_PERMISSION);
  const canManageNotifications = isAdmin || auth.hasPermission('manage_roles') || auth.hasPermission('create_surveys');

  return useMemo(() => ([
    {
      key: 'close-dates',
      show: canManageAllEmployees,
      path: '/admin/close-dates',
      icon: 'bi-calendar-x',
      title: 'Edit Close Dates',
      description: 'Create and manage company close dates.',
    },
    {
      key: 'hours-breakdown',
      show: isAdmin,
      path: '/admin/hours-breakdown',
      icon: 'bi-clock-history',
      title: 'Hours Breakdown',
      description: 'Pull one person’s TimeCamp hours for a date range and filter them task by task.',
    },
    {
      key: 'vendors',
      show: canManageVendors,
      path: '/admin/vendors-customers',
      icon: 'bi-shop',
      title: 'Vendors & Customers',
      description: 'Review, rename, and consolidate the vendor and customer lists used on expenses.',
    },
    {
      key: 'mileage-rates',
      show: canManageMileageRates,
      path: '/admin/mileage-rates',
      icon: 'bi-speedometer2',
      title: 'Mileage Rates',
      description: 'Set the IRS mileage rate, by effective date, that mileage expenses are priced at.',
    },
    {
      // Admin only while the timesheet is in preview; manage_timesheets once
      // it opens up (see docs/timesheet-system.md).
      key: 'timesheet',
      show: isAdmin,
      path: '/admin/timesheet',
      icon: 'bi-stopwatch',
      title: 'Timesheet',
      description: 'Projects and tasks, timesheet settings and the source of hours, and the TimeCamp import.',
    },
    {
      key: 'time-off',
      show: canManageTimeOff,
      path: '/admin/time-off',
      icon: 'bi-airplane',
      title: 'Time Off',
      description: 'The PATO hours everyone is preloaded with.',
    },
    {
      key: 'notifications',
      show: canManageNotifications,
      path: '/admin/notifications',
      icon: 'bi-envelope',
      title: 'Notifications',
      description: 'Approval and survey reminder email schedules.',
    },
  ].filter((item) => item.show)), [
    canManageAllEmployees,
    isAdmin,
    canManageVendors,
    canManageMileageRates,
    canManageTimeOff,
    canManageNotifications,
  ]);
};

export default function ConfigurationPage() {
  const items = useConfigurationItems();

  return (
    <ChoiceWheel
      items={items}
      title="Configuration"
      back={{ label: 'Admin', path: '/admin' }}
      emptyText="You do not have access to any configuration tools."
    />
  );
}
